"use client";

// ============================================================
// CLUBHUB – Dokumente: der Bestand im Browser
// ============================================================
// Ordner, Dateien und Vorlagen liegen hier, damit eine Aenderung
// sofort zu sehen ist. Beide Seiten der Ablage (`/dokumente`,
// `/dokumente/vorlagen`) lesen denselben Speicher.
//
// MUSTERBESTAND (ohne Sitzung): Es gibt kein Backend, das sich
// etwas merkt. Angelegt, umbenannt, verschoben und geloescht wird
// nur hier; der Stand ueberlebt den Wechsel zwischen den Seiten,
// Neuladen setzt ihn zurueck — genau das soll eine Maske tun.
//
// DATENBANK (mit Sitzung): Jede Aenderung geht zuerst als Server
// Action an den Server; hier steht sie nur, bis der frische Stand
// vom Server kommt (`router.refresh()`). Der Speicher ist dafuer an
// die Props GEBUNDEN, aus denen er entstand: kommen neue Props,
// gilt wieder der Server.
// ============================================================

import { useCallback, useSyncExternalStore } from "react";
import type { DocumentView, FolderView, TemplateView } from "@/lib/dokumente/views";

export type Bestand = {
  folders: FolderView[];
  documents: DocumentView[];
  templates: TemplateView[];
};

let stand: Bestand | null = null;
/** Aus welchen Props `stand` entstand (zaehlt nur mit Datenbank). */
let basis: Bestand | null = null;
const hoerer = new Set<() => void>();

function abonnieren(h: () => void) {
  hoerer.add(h);
  return () => {
    hoerer.delete(h);
  };
}

/** Mit Datenbank gilt eine Aenderung hier nur, bis neue Props kommen (`quelle` traegt `AblageDaten`). */
function gebunden(anfang: Bestand): boolean {
  return (anfang as Bestand & { quelle?: string }).quelle === "datenbank";
}

function gilt(anfang: Bestand): Bestand {
  return stand && (!gebunden(anfang) || basis === anfang) ? stand : anfang;
}

/**
 * Der Bestand und der Weg, ihn zu aendern. `anfang` muss eine
 * stabile Referenz sein (die Props der Seite) — sie ist der Stand,
 * solange noch nichts geaendert wurde, und der Stand beim ersten
 * Zeichnen auf dem Server.
 */
export function useBestand(anfang: Bestand) {
  const bestand = useSyncExternalStore(
    abonnieren,
    () => gilt(anfang),
    () => anfang,
  );
  const aendern = useCallback(
    (fn: (b: Bestand) => Bestand) => {
      stand = fn(gilt(anfang));
      basis = anfang;
      for (const h of hoerer) h();
    },
    [anfang],
  );
  return { bestand, aendern };
}

/** Der Stand ausserhalb von React — fuer Arbeit, die einen Seitenwechsel ueberlebt (Hochladen). */
export function bestandJetzt(): Bestand | null {
  return stand;
}

/**
 * Aendern ohne Komponente — das Ende eines Hochladens kommt womoeglich,
 * wenn die Seite, die es begonnen hat, schon weg ist. Wirkt nur, wenn
 * der Bestand schon festgehalten ist (`festhalten`).
 */
export function aendernSpeicher(fn: (b: Bestand) => Bestand) {
  if (!stand) return;
  stand = fn(stand);
  for (const h of hoerer) h();
}

/** Uebernimmt den Anfangsstand in den Speicher, ohne etwas zu aendern (oder neue Props). */
export function festhalten(anfang: Bestand) {
  if (!stand || (gebunden(anfang) && basis !== anfang)) {
    stand = anfang;
    basis = anfang;
  }
}

let zaehler = 0;
/** Eine Kennung fuer etwas, das hier entsteht (spaeter vergibt sie die Datenbank). */
export function neueId(praefix: string): string {
  zaehler += 1;
  return `${praefix}-neu-${Date.now().toString(36)}${zaehler.toString(36)}`;
}

// ── Aenderungen als reine Funktionen ─────────────────────────

export function mitDatei(b: Bestand, id: string, fn: (d: DocumentView) => DocumentView): Bestand {
  return { ...b, documents: b.documents.map((d) => (d.id === id ? fn(d) : d)) };
}

export function mitOrdner(b: Bestand, id: string, fn: (f: FolderView) => FolderView): Bestand {
  return { ...b, folders: b.folders.map((f) => (f.id === id ? fn(f) : f)) };
}

export function ohneDateien(b: Bestand, ids: string[]): Bestand {
  const weg = new Set(ids);
  return { ...b, documents: b.documents.filter((d) => !weg.has(d.id)) };
}

export function ohneOrdner(b: Bestand, folderIds: string[]): Bestand {
  const weg = new Set(folderIds);
  return {
    ...b,
    folders: b.folders.filter((f) => !weg.has(f.id)),
    documents: b.documents.filter((d) => !d.folderId || !weg.has(d.folderId)),
  };
}

/** Holt Geloeschtes zurueck — fuer „Rückgängig“ im Toast. */
export function zurueck(b: Bestand, folders: FolderView[], documents: DocumentView[]): Bestand {
  const daF = new Set(b.folders.map((f) => f.id));
  const daD = new Set(b.documents.map((d) => d.id));
  return {
    ...b,
    folders: [...b.folders, ...folders.filter((f) => !daF.has(f.id))],
    documents: [...b.documents, ...documents.filter((d) => !daD.has(d.id))],
  };
}
